import React, { useCallback } from "react";
import { useMutation } from "@apollo/client";
import { FormattedMessage } from "react-intl";
import {
  ADD_LEGACY_TERM_SHEET,
  ADD_LEGACY_TERM_SHEET_ASSIGNMENTS,
  REMOVE_LEGACY_TERM_SHEET,
  REMOVE_LEGACY_TERM_SHEET_ASSIGNMENT,
  UPDATE_LEGACY_TERM_SHEET,
  UPDATE_LEGACY_TERM_SHEET_ASSIGNMENTS,
} from "graphql/mutations";
import { GET_LEGACY_TERM_SHEETS } from "graphql/queries";
import { useModalConfirmation, useQueryData, useToast } from "hooks";
import { getTransformedParams, isActiveTermSheet } from "constants/index";
import { Row, Col } from "components/Containers";
import Icon, { ICON_TYPE } from "components/Icon";
import { colors } from "style";
import { MODAL_SIZE } from "components/Modal";
import { toUcFirst } from "utils";

const keyName = "legacyTermSheets";

export default function useLegacyTermSheet({ teamId } = {}) {
  const { toast } = useToast();
  const { show } = useModalConfirmation();
  const { loading, data, error, refetch, refetching } = useQueryData({
    queryName: GET_LEGACY_TERM_SHEETS,
    keyName,
    variables: { teamId },
    skip: !teamId,
  });

  const [handleAddMembers, { loading: addingMembers }] = useMutation(ADD_LEGACY_TERM_SHEET_ASSIGNMENTS);
  const [handleRemoveMember, { loading: removingMember }] = useMutation(REMOVE_LEGACY_TERM_SHEET_ASSIGNMENT);
  const [handleUpdateAssignments, { loading: updatingAssignments }] = useMutation(UPDATE_LEGACY_TERM_SHEET_ASSIGNMENTS);
  const [handleAddLegacyTermSheet, { loading: addingTermSheet }] = useMutation(ADD_LEGACY_TERM_SHEET, {
    refetchQueries: [GET_LEGACY_TERM_SHEETS],
  });
  const [handleUpdateLegacyTermSheet, { loading: updatingTermSheet }] = useMutation(UPDATE_LEGACY_TERM_SHEET);
  const [handleRemoveLegacyTermSheet, { loading: removingTermSheet }] = useMutation(REMOVE_LEGACY_TERM_SHEET, {
    refetchQueries: [GET_LEGACY_TERM_SHEETS],
  });

  const handleAddMembersToTermSheet = useCallback(
    async (termSheet, profileIds) =>
      await handleAddMembers({
        variables: {
          input: {
            versionId: termSheet?.versionId,
            profileIds,
          },
        },
        update: (cache, { data }) => {
          const newAssignments = data?.addLegacyTermSheetAssignments?.assignments;

          if (newAssignments?.length) {
            // Update the cache with the new termSheet data
            cache.modify({
              id: cache.identify(termSheet),
              fields: {
                assignments() {
                  return newAssignments;
                },
              },
            });
          }
        },
        onCompleted: () => toast.success(<FormattedMessage id={"TermSheets.LegacyTermSheetTable.AddMember.Success"} />),
        onError: () => toast.error(<FormattedMessage id={"TermSheets.LegacyTermSheetTable.AddMember.Error"} />),
      }),
    [handleAddMembers, toast]
  );

  /**
   * Update assignments with pending changes
   * @param {Object} termSheet - The termsheet to update
   * @param {Object} changes - The pending changes to save
   */
  const handleSaveAssignments = useCallback(
    async (termSheet, changes, onComplete) => {
      const isTermSheetActive = isActiveTermSheet(termSheet);
      const response = isTermSheetActive
        ? await show(
            <FormattedMessage id="TermSheets.LegacyTermSheetTable.UpdateMember.Confirmation" />,
            <FormattedMessage id="TermSheets.LegacyTermSheetTable.UpdateMember.ConfirmationDetails" />,
            {
              showCancel: true,
              showSave: true,
              primaryButtonMessageId: "Global.Yes",
            }
          )
        : true;

      if (response) {
        await handleUpdateAssignments({
          variables: {
            input: {
              versionId: termSheet?.versionId,
              params: getTransformedParams(changes, termSheet?.assignments),
            },
          },
          update: (cache, { data }) => {
            const updatedAssignments = data?.updateLegacyTermSheetAssignments?.assignments;

            if (updatedAssignments?.length) {
              cache.modify({
                id: cache.identify(termSheet),
                fields: {
                  assignments() {
                    return updatedAssignments;
                  },
                },
              });
            }
          },
          onCompleted: () => {
            toast.success(<FormattedMessage id="TermSheets.LegacyTermSheetTable.UpdateMember.Success" />);
            onComplete?.();
          },
          onError: async (errors) =>
            await showDetailedError(
              errors,
              "TermSheets.LegacyTermSheetTable.UpdateMember.ErrorTitle",
              "TermSheets.LegacyTermSheetTable.UpdateMember.ErrorDescription"
            ),
        });
      }
    },
    [handleUpdateAssignments, toast, showDetailedError, show]
  );

  const handleRemoveMemberFromTermSheet = useCallback(
    async (termSheet, assignment) => {
      const response = await show(
        <FormattedMessage id="TermSheets.LegacyTermSheetTable.RemoveMember.Confirmation" />,
        <FormattedMessage
          id="TermSheets.LegacyTermSheetTable.RemoveMember.ConfirmationDetails"
          values={{
            name: <b>{assignment.profile.name}</b>,
            scenario: (
              <b>
                <FormattedMessage
                  id="Squads.Squad.TermSheet.Table.Header.Scenario"
                  values={{ versionNumber: termSheet?.versionNumber }}
                />
              </b>
            ),
          }}
        />,
        {
          showCancel: true,
          showSave: true,
          primaryButtonMessageId: "Global.Yes",
        }
      );

      if (response) {
        handleRemoveMember({
          variables: {
            input: {
              assignmentId: assignment.id,
            },
          },
          update: (cache, { data }) => {
            const remainingAssignments = data?.removeLegacyTermSheetAssignment?.assignments;

            if (remainingAssignments) {
              // Update the cache with the new termSheet data
              cache.modify({
                id: cache.identify(termSheet),
                fields: {
                  assignments() {
                    return remainingAssignments;
                  },
                },
              });
            }
          },
          onCompleted: () =>
            toast.success(<FormattedMessage id={"TermSheets.LegacyTermSheetTable.RemoveMember.Success"} />),
          onError: () => toast.error(<FormattedMessage id={"TermSheets.LegacyTermSheetTable.RemoveMember.Error"} />),
        });
      }
    },
    [handleRemoveMember, show, toast]
  );

  const handleAddTermSheet = useCallback(
    async (teamId, versionType, versionId = null) =>
      await handleAddLegacyTermSheet({
        variables: {
          input: {
            teamId,
            versionId,
            versionType,
          },
        },
        onCompleted: () =>
          toast.success(
            <FormattedMessage
              id={`TermSheets.LegacyTermSheetTable.${versionId ? "Duplicate" : "Add"}TermSheet.Success`}
            />
          ),
        onError: () => toast.error(<FormattedMessage id="TermSheets.LegacyTermSheetTable.AddTermSheet.Error" />),
      }),
    [handleAddLegacyTermSheet, teamId, toast, refetch]
  );

  const handleUpdateTermSheet = useCallback(
    async (id, params) =>
      await handleUpdateLegacyTermSheet({
        variables: {
          input: {
            id,
            params,
          },
        },
        onCompleted: () =>
          toast.success(<FormattedMessage id="TermSheets.LegacyTermSheetTable.UpdateTermSheet.Success" />),
        onError: async (errors) =>
          await showDetailedError(
            errors,
            "TermSheets.LegacyTermSheetTable.UpdateTermSheet.ErrorTitle",
            "TermSheets.LegacyTermSheetTable.UpdateTermSheet.ErrorDescription"
          ),
      }),
    [handleUpdateLegacyTermSheet, toast, showDetailedError]
  );

  const handleRemoveTermSheet = useCallback(
    async (termSheet) => {
      const response = await show(
        <FormattedMessage id="TermSheets.LegacyTermSheetTable.RemoveTermSheet.Confirmation" />,
        <FormattedMessage
          id="TermSheets.LegacyTermSheetTable.RemoveTermSheet.ConfirmationDetails"
          values={{
            scenario: (
              <b>
                <FormattedMessage
                  id="Squads.Squad.TermSheet.Table.Header.Scenario"
                  values={{ versionNumber: termSheet?.versionNumber }}
                />
              </b>
            ),
          }}
        />,
        {
          showCancel: true,
          showSave: true,
          primaryButtonMessageId: "Global.Yes",
        }
      );

      if (response) {
        await handleRemoveLegacyTermSheet({
          variables: {
            input: {
              versionId: termSheet?.versionId,
            },
          },
          onCompleted: () =>
            toast.success(<FormattedMessage id={"TermSheets.LegacyTermSheetTable.RemoveTermSheet.Success"} />),
          onError: () => toast.error(<FormattedMessage id={"TermSheets.LegacyTermSheetTable.RemoveTermSheet.Error"} />),
        });
      }
    },
    [handleRemoveLegacyTermSheet, show, toast]
  );

  /**
   * Reusable error handler that displays detailed GraphQL errors in a modal
   * @param {Object} errors - GraphQL errors object
   * @param {String} titleId - Translation ID for modal title
   * @param {String} descriptionId - Translation ID for error description
   */
  const showDetailedError = useCallback(
    async (errors, titleId, descriptionId) => {
      await show(
        <FormattedMessage id={titleId} />,
        <>
          <p>
            <FormattedMessage id={descriptionId} />
          </p>
          <p>
            <b>
              <FormattedMessage id="TermSheets.LegacyTermSheetTable.ErrorDescriptionIssuesCopy" />
            </b>
          </p>
          <Col gap="0.5rem">
            {errors?.graphQLErrors
              ?.flatMap((error) => error.extensions?.errors ?? [])
              ?.map((detailedError, index) => (
                <Row key={index} noWrap>
                  <Icon type={ICON_TYPE.unavailable} color={colors.red} />
                  <span>
                    {toUcFirst(detailedError?.field)}: {detailedError?.message}
                  </span>
                </Row>
              ))}
          </Col>
        </>,
        {
          showCancel: true,
          showSave: false,
          secondaryButtonMessageId: "Global.Close",
          size: MODAL_SIZE.small,
        }
      );
    },
    [show]
  );

  return {
    loading: loading || refetching,
    data: data && data[keyName]?.nodes,
    buyInCurrency: data && data[keyName]?.buyInCurrency,
    sellInCurrency: data && data[keyName]?.sellInCurrency,
    error,
    refetch,
    handleAddMembersToTermSheet,
    addingMembers,
    handleSaveAssignments,
    updatingAssignments,
    handleRemoveMemberFromTermSheet,
    removingMember,
    handleAddTermSheet,
    addingTermSheet,
    handleUpdateTermSheet,
    updatingTermSheet,
    handleRemoveTermSheet,
    removingTermSheet,
  };
}
